import rt from "aldoo-ra/Realtime"

/**
 * Realtime-based deployment controller
 * Replaces the old HTTP polling approach with Socket.io real-time communication
 */
function createRealtimeDeploymentController() {
  let deploymentSocket = null
  let subscribers = new Set()
  let isInitialized = false
  let currentEnvironment = null
  let eventCallbacks = null
  let hasActiveDeployments = false
  let reconnectTimer = null
  let isReconnecting = false

  // Update deployment activity state
  const updateDeploymentActivity = (statusMap) => {
    if (!statusMap) return
    
    // Check if any apps have active deployments
    const activeDeployments = Array.from(statusMap.values()).some(app => 
      app.deploymentState?.status === 'running' ||
      (app.deploymentState?.progress > 0 && app.deploymentState?.progress < 100) ||
      app.updateTask
    )
    
    hasActiveDeployments = activeDeployments
  }

  // Attempt to reconnect when needed
  const attemptReconnection = async () => {
    if (isReconnecting || reconnectTimer) return // Already attempting

    // Don't reconnect if already connected
    if (deploymentSocket?.connected) {
      return
    }

    isReconnecting = true

    try {
      // Clean up old connection ONLY if it exists and is disconnected
      if (deploymentSocket && !deploymentSocket.connected) {
        deploymentSocket.removeAllListeners()
        deploymentSocket.disconnect()
        deploymentSocket = null
      }
      isInitialized = false // Reset initialization flag

      // Wait a moment before reconnecting
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Re-initialize
      const newSocket = initialize()
      if (!newSocket) {
        throw new Error('Failed to initialize new socket connection')
      }

      // Re-setup event listeners if we had them
      if (eventCallbacks) {
        setupEventListeners(eventCallbacks)
      }

      // Re-subscribe to environment if we were subscribed
      if (currentEnvironment) {
        await subscribeToEnvironment(currentEnvironment)
      }

      isReconnecting = false

    } catch (error) {
      console.error('❌ Deployment viewer: Reconnection failed:', error.message)
      isReconnecting = false

      // Retry after delay
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        attemptReconnection()
      }, 5000) // Retry every 5 seconds
    }
  }

  // Initialize socket connection
  const initialize = () => {
    if (isInitialized) return deploymentSocket

    try {
      // rt() handles authentication automatically, just like api()
      deploymentSocket = rt('deployment')
      isInitialized = true
      
      return deploymentSocket
      
    } catch (error) {
      console.error('Realtime Deployment Controller: Failed to connect:', error)
      throw error
    }
  }

  // Subscribe to environment updates
  const subscribeToEnvironment = async (environment, callback) => {
    if (!deploymentSocket) {
      throw new Error('Controller not initialized. Call initialize() first.')
    }
    
    // Store current environment for reconnection
    currentEnvironment = environment

    return new Promise((resolve, reject) => {
      deploymentSocket.emit('deployment:subscribe_environment', { 
        app_env: environment 
      }, (response) => {
        if (response.subscribed || response.success) {
          
          // Store callback for updates
          if (callback) {
            subscribers.add(callback)
          }
          
          resolve(response)
        } else {
          reject(new Error(response.error || 'Failed to subscribe'))
        }
      })
    })
  }

  // Get deployment tool connection status
  const getConnectionStatus = async () => {
    if (!deploymentSocket) {
      throw new Error('Controller not initialized. Call initialize() first.')
    }

    return new Promise((resolve, reject) => {
      deploymentSocket.emit('deployment:get_connection_status', {}, (response) => {
        if (response.success) {
          resolve(response)
        } else {
          reject(new Error(response.error || 'Failed to get connection status'))
        }
      })
    })
  }

  // Get app list

  // Get app statuses (simplified - no more complex polling)
  const getAppStatuses = async (appList) => {
    if (!deploymentSocket) {
      throw new Error('Controller not initialized. Call initialize() first.')
    }


    if (!appList || (Array.isArray(appList) && appList.length === 0)) {
      return { success: true, data: {} }
    }

    // Ensure appList is an array
    if (!Array.isArray(appList)) {
      console.error('getAppStatuses: appList is not an array, received:', appList)
      return { success: false, error: 'Invalid app list format' }
    }

    const appIds = appList.map(app => app.id)
    
    return new Promise((resolve, reject) => {
      deploymentSocket.emit('deployment:get_app_updates', { 
        app_ids: appIds 
      }, (response) => {
        if (response.success) {
          resolve(response)
        } else {
          reject(new Error(response.error || 'Failed to get app statuses'))
        }
      })
    })
  }

  // Reset app error state and trigger a fresh scan
  const resetAppError = async (appId) => {
    if (!deploymentSocket) {
      throw new Error('Controller not initialized. Call initialize() first.')
    }

    return new Promise((resolve, reject) => {
      deploymentSocket.emit('deployment:reset_app_error', { app_id: appId }, (response) => {
        if (response.success) {
          resolve(response)
        } else {
          reject(new Error(response.error || 'Failed to reset app error'))
        }
      })
    })
  }

  // Run app update/deployment
  const runAppUpdate = async (appId, waitForCompletion = false) => {
    if (!deploymentSocket) {
      throw new Error('Controller not initialized. Call initialize() first.')
    }

    return new Promise((resolve, reject) => {
      const payload = { 
        app_id: appId,
        wait: waitForCompletion 
      }
      
      deploymentSocket.emit('deployment:run_update', payload, (response) => {
        if (response.success) {
          resolve(response)
        } else {
          reject(new Error(response.error || 'Failed to start deployment'))
        }
      })
    })
  }

  // Setup event listeners for real-time updates
  const setupEventListeners = (callbacks = {}) => {
    if (!deploymentSocket) {
      throw new Error('Controller not initialized. Call initialize() first.')
    }

    // Store callbacks for reconnection
    eventCallbacks = callbacks

    const {
      onAppUpdates,
      onDeploymentComplete,
      onDeploymentFailed,
      onTaskUpdate,
      onToolConnectionStatus
    } = callbacks

    // Listen for immediate deployment tool connection status (sent on connect)
    if (onToolConnectionStatus) {
      deploymentSocket.on('deployment:tool_connection_status', (data) => {
        onToolConnectionStatus(data)
      })
    }

    // Listen for app updates
    if (onAppUpdates) {
      deploymentSocket.on('deployment:app_updates', (data) => {
        onAppUpdates(data)
      })
    }

    // Listen for deployment completion
    if (onDeploymentComplete) {
      deploymentSocket.on('deployment:deployment_complete', (data) => {
        onDeploymentComplete(data)
      })
    }

    // Listen for deployment failures
    if (onDeploymentFailed) {
      deploymentSocket.on('deployment:deployment_failed', (data) => {
        onDeploymentFailed(data)
      })
    }

    // Listen for task updates
    if (onTaskUpdate) {
      deploymentSocket.on('deployment:task_update', (data) => {
        onTaskUpdate(data)
      })

      // Also listen for task progress updates from the API server
      deploymentSocket.on('deployment:task_progress', (data) => {
        onTaskUpdate(data)
      })

      // Listen for task completion from the API server
      deploymentSocket.on('deployment:task_completed', (data) => {
        if (onDeploymentComplete) {
          onDeploymentComplete(data)
        } else {
          onTaskUpdate(data)
        }
      })
    }

    // Connection status listeners
    deploymentSocket.on('connect', () => {
      // Notify the UI that we've connected
      if (onToolConnectionStatus) {
        onToolConnectionStatus({
          success: true,
          connection: {
            connected: true
          }
        })
      }

      // Clear any pending reconnection timer
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }

      // Re-subscribe to environment on reconnection.
      // In Socket.io v4, 'reconnect' fires on the Manager not the socket,
      // so we handle re-subscription here where currentEnvironment is already set.
      if (currentEnvironment) {
        subscribeToEnvironment(currentEnvironment)
      }
    })

    deploymentSocket.on('disconnect', (reason) => {
      // Notify the UI that we've disconnected
      if (onToolConnectionStatus) {
        onToolConnectionStatus({
          success: true,
          connection: {
            connected: false,
            reason: reason
          }
        })
      }

      // Only attempt reconnection if it's a real disconnection (not manual)
      if (reason !== 'io client disconnect' && !isReconnecting) {
        // Start reconnection attempts after a short delay
        setTimeout(() => attemptReconnection(), 2000)
      }
    })

    deploymentSocket.on('connect_error', (error) => {
      // Notify the UI that we have a connection error
      if (onToolConnectionStatus) {
        onToolConnectionStatus({
          success: true,
          connection: {
            connected: false,
            error: error.message
          }
        })
      }

      // Only log critical connection errors, not websocket failures
      if (error.message !== 'websocket error') {
        console.error('❌ Deployment viewer: Connection error:', error.message)
      }

      // Only attempt reconnection if not already reconnecting and not already connected
      if (!isReconnecting && !deploymentSocket?.connected) {
        setTimeout(() => attemptReconnection(), 3000)
      }
    })

    deploymentSocket.on('reconnect', (attemptNumber) => {
      // Notify the UI that we've reconnected
      if (onToolConnectionStatus) {
        onToolConnectionStatus({
          success: true,
          connection: {
            connected: true
          }
        })
      }

      // Re-subscribe to environment if needed
      if (currentEnvironment) {
        subscribeToEnvironment(currentEnvironment)
      }
    })
  }

  // Cleanup
  const cleanup = () => {
    // Clear reconnection timer
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    
    if (deploymentSocket) {
      deploymentSocket.disconnect()
      deploymentSocket = null
    }
    
    subscribers.clear()
    isInitialized = false
    isReconnecting = false
    currentEnvironment = null
    eventCallbacks = null
    hasActiveDeployments = false
  }

  // Public API - dramatically simplified compared to the old controller
  return {
    initialize,
    subscribeToEnvironment,
    getConnectionStatus,
    getAppStatuses,
    runAppUpdate,
    resetAppError,
    setupEventListeners,
    updateDeploymentActivity,
    cleanup,

    // Getters
    // Lazy: the Deployment page calls initialize() itself, but the same
    // dialogs open from the Fleet page too — a caller must never see null
    // here ("Cannot read properties of null (reading 'emit')" on Restart).
    get socket() {
      if (!deploymentSocket) { try { initialize() } catch { return null } }
      return deploymentSocket
    },

    get isConnected() {
      return deploymentSocket?.connected || false
    },

    get isInitialized() {
      return isInitialized
    }
  }
}

// Export singleton instance
let controllerInstance = null

export default function getRealtimeDeploymentController() {
  if (!controllerInstance) {
    controllerInstance = createRealtimeDeploymentController()
  }
  return controllerInstance
}